import { Controller } from "@hotwired/stimulus"

// Controller for managing the split-pane page builder with live preview
export default class extends Controller {
  static targets = [
    "container",
    "editorPanel",
    "previewPanel",
    "previewFrame",
    "frameContainer",
    "deviceButton",
    "deviceContainer",
    "toggleButton",
    "refreshIndicator",
    "previewToggle",
    "refreshButton",
    "resizeHandle"
  ]

  static values = {
    editorWidth: { type: Number, default: 50 },
    refreshDelay: { type: Number, default: 500 }
  }

  connect() {
    this.isResizing = false
    this.currentDevice = "desktop"
    this.isMobileViewingPreview = false
    this.pendingRefresh = false
    this.previewVisible = true

    // Set initial device button state
    this.updateDeviceButtonStates()
    this.updatePreviewToggleState()

    // Listen for content changes to refresh preview
    this.setupContentChangeListeners()
  }

  disconnect() {
    this.removeContentChangeListeners()
  }

  // Device preview methods
  setDevice(event) {
    const device = event.params.device
    this.currentDevice = device
    this.updateDeviceButtonStates()
    this.updatePreviewFrameSize()
  }

  updateDeviceButtonStates() {
    this.deviceButtonTargets.forEach(button => {
      const buttonDevice = button.dataset.previewPanelDeviceParam
      if (buttonDevice === this.currentDevice) {
        button.classList.add("bg-white", "dark:bg-gray-600", "text-blue-600", "dark:text-blue-400", "shadow-sm")
        button.classList.remove("text-gray-600", "dark:text-gray-400", "hover:bg-gray-200", "dark:hover:bg-gray-600")
      } else {
        button.classList.remove("bg-white", "dark:bg-gray-600", "text-blue-600", "dark:text-blue-400", "shadow-sm")
        button.classList.add("text-gray-600", "dark:text-gray-400", "hover:bg-gray-200", "dark:hover:bg-gray-600")
      }
    })
  }

  updatePreviewFrameSize() {
    if (!this.hasFrameContainerTarget) return

    const container = this.frameContainerTarget

    switch (this.currentDevice) {
      case "mobile":
        container.style.maxWidth = "375px"
        container.style.margin = "0 auto"
        break
      case "tablet":
        container.style.maxWidth = "768px"
        container.style.margin = "0 auto"
        break
      case "desktop":
      default:
        container.style.maxWidth = "100%"
        container.style.margin = "0"
        break
    }
  }

  // Mobile toggle between edit and preview
  toggleMobileView() {
    this.isMobileViewingPreview = !this.isMobileViewingPreview

    if (this.isMobileViewingPreview) {
      this.editorPanelTarget.classList.add("hidden")
      this.previewPanelTarget.classList.remove("hidden")
      this.previewPanelTarget.classList.add("block", "w-full")
      this.updateToggleButtonLabel(true)
    } else {
      this.editorPanelTarget.classList.remove("hidden")
      this.previewPanelTarget.classList.add("hidden")
      this.previewPanelTarget.classList.remove("block", "w-full")
      this.updateToggleButtonLabel(false)
    }
  }

  updateToggleButtonLabel(showingPreview) {
    if (!this.hasToggleButtonTarget) return

    const editLabel = this.toggleButtonTarget.querySelector(".edit-label")
    const previewLabel = this.toggleButtonTarget.querySelector(".preview-label")

    if (showingPreview) {
      editLabel?.classList.add("hidden")
      previewLabel?.classList.remove("hidden")
    } else {
      editLabel?.classList.remove("hidden")
      previewLabel?.classList.add("hidden")
    }
  }

  // Toggle preview panel visibility (desktop)
  togglePreview() {
    this.previewVisible = !this.previewVisible

    if (this.previewVisible) {
      // Show preview panel
      this.previewPanelTarget.classList.remove("hidden")
      this.previewPanelTarget.classList.add("md:block")
      this.editorPanelTarget.classList.remove("md:w-full")
      this.editorPanelTarget.classList.add("md:w-1/2")

      // Show resize handle
      const resizeHandle = this.element.querySelector("#resize-handle")
      if (resizeHandle) {
        resizeHandle.classList.remove("hidden")
        resizeHandle.classList.add("md:flex")
      }

      // Show device buttons container
      if (this.hasDeviceContainerTarget) {
        this.deviceContainerTarget.classList.remove("hidden")
        this.deviceContainerTarget.classList.add("md:flex")
      }

      // Refresh preview to show latest content
      this.refreshPreview()
    } else {
      // Hide preview panel
      this.previewPanelTarget.classList.add("hidden")
      this.previewPanelTarget.classList.remove("md:block")
      this.editorPanelTarget.classList.add("md:w-full")
      this.editorPanelTarget.classList.remove("md:w-1/2")

      // Hide resize handle
      const resizeHandle = this.element.querySelector("#resize-handle")
      if (resizeHandle) {
        resizeHandle.classList.add("hidden")
        resizeHandle.classList.remove("md:flex")
      }

      // Hide device buttons container
      if (this.hasDeviceContainerTarget) {
        this.deviceContainerTarget.classList.add("hidden")
        this.deviceContainerTarget.classList.remove("md:flex")
      }
    }

    this.updatePreviewToggleState()
  }

  updatePreviewToggleState() {
    if (!this.hasPreviewToggleTarget) return

    const onLabel = this.previewToggleTarget.querySelector(".preview-on-label")
    const offLabel = this.previewToggleTarget.querySelector(".preview-off-label")

    if (this.previewVisible) {
      // Preview is visible - show active state
      this.previewToggleTarget.classList.add("bg-blue-600", "text-white", "hover:bg-blue-700")
      this.previewToggleTarget.classList.remove("text-gray-600", "dark:text-gray-300", "hover:text-gray-900", "dark:hover:text-white", "border", "border-gray-300", "dark:border-gray-600")
      onLabel?.classList.remove("hidden")
      offLabel?.classList.add("hidden")

      // Show refresh button
      if (this.hasRefreshButtonTarget) {
        this.refreshButtonTarget.classList.remove("hidden")
      }
    } else {
      // Preview is hidden - show inactive state
      this.previewToggleTarget.classList.remove("bg-blue-600", "text-white", "hover:bg-blue-700")
      this.previewToggleTarget.classList.add("text-gray-600", "dark:text-gray-300", "hover:text-gray-900", "dark:hover:text-white", "border", "border-gray-300", "dark:border-gray-600")
      onLabel?.classList.add("hidden")
      offLabel?.classList.remove("hidden")

      // Hide refresh button when preview is hidden
      if (this.hasRefreshButtonTarget) {
        this.refreshButtonTarget.classList.add("hidden")
      }
    }
  }

  // Resize handle methods
  startResize(event) {
    event.preventDefault()
    this.isResizing = true
    this.startX = event.clientX
    this.startWidth = this.editorPanelTarget.offsetWidth

    document.addEventListener("mousemove", this.handleResize)
    document.addEventListener("mouseup", this.stopResize)
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"
  }

  handleResize = (event) => {
    if (!this.isResizing) return

    const containerWidth = this.containerTarget.offsetWidth
    const deltaX = event.clientX - this.startX
    const newWidth = this.startWidth + deltaX

    // Constrain width between 30% and 70%
    const minWidth = containerWidth * 0.3
    const maxWidth = containerWidth * 0.7
    const constrainedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth))

    const percentage = (constrainedWidth / containerWidth) * 100
    this.editorPanelTarget.style.width = `${percentage}%`
    this.editorWidthValue = percentage
  }

  stopResize = () => {
    this.isResizing = false
    document.removeEventListener("mousemove", this.handleResize)
    document.removeEventListener("mouseup", this.stopResize)
    document.body.style.cursor = ""
    document.body.style.userSelect = ""
  }

  // Preview refresh methods
  refreshPreview() {
    if (!this.hasPreviewFrameTarget) return

    this.showRefreshIndicator()

    // Add a cache-busting param to force refresh
    const currentSrc = this.previewFrameTarget.src
    const url = new URL(currentSrc)
    url.searchParams.set("_refresh", Date.now())
    this.previewFrameTarget.src = url.toString()

    // Hide indicator when iframe loads
    this.previewFrameTarget.onload = () => {
      this.hideRefreshIndicator()
    }
  }

  // Debounced refresh - call this when content changes
  scheduleRefresh() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout)
    }

    this.pendingRefresh = true
    this.showRefreshIndicator()

    this.refreshTimeout = setTimeout(() => {
      this.pendingRefresh = false
      this.refreshPreview()
    }, this.refreshDelayValue)
  }

  showRefreshIndicator() {
    if (this.hasRefreshIndicatorTarget) {
      this.refreshIndicatorTarget.classList.remove("hidden")
    }
  }

  hideRefreshIndicator() {
    if (this.hasRefreshIndicatorTarget) {
      this.refreshIndicatorTarget.classList.add("hidden")
    }
  }

  // Content change listeners for auto-refresh
  setupContentChangeListeners() {
    // Listen for Turbo form submissions to refresh preview
    this.boundHandleFormSubmit = this.handleFormSubmit.bind(this)
    document.addEventListener("turbo:submit-end", this.boundHandleFormSubmit)

    // Listen for custom content-changed events (from CKEditor autosave, etc.)
    this.boundHandleContentChanged = this.handleContentChanged.bind(this)
    document.addEventListener("preview:content-changed", this.boundHandleContentChanged)

    // Listen for Turbo stream updates (row reordering, etc.)
    this.boundHandleTurboStream = this.handleTurboStream.bind(this)
    document.addEventListener("turbo:before-stream-render", this.boundHandleTurboStream)

    // Listen for input changes on forms within the editor panel
    this.boundHandleInputChange = this.handleInputChange.bind(this)
    if (this.hasEditorPanelTarget) {
      this.editorPanelTarget.addEventListener("change", this.boundHandleInputChange)
    }
  }

  removeContentChangeListeners() {
    if (this.boundHandleFormSubmit) {
      document.removeEventListener("turbo:submit-end", this.boundHandleFormSubmit)
    }
    if (this.boundHandleContentChanged) {
      document.removeEventListener("preview:content-changed", this.boundHandleContentChanged)
    }
    if (this.boundHandleTurboStream) {
      document.removeEventListener("turbo:before-stream-render", this.boundHandleTurboStream)
    }
    if (this.boundHandleInputChange && this.hasEditorPanelTarget) {
      this.editorPanelTarget.removeEventListener("change", this.boundHandleInputChange)
    }
  }

  handleFormSubmit(event) {
    // Only refresh if the form submission was successful
    if (event.detail.success) {
      this.scheduleRefresh()
    }
  }

  handleContentChanged(event) {
    // Content was changed via autosave or other means
    this.scheduleRefresh()
  }

  handleTurboStream(event) {
    // A Turbo Stream is about to render - schedule refresh after it completes
    this.scheduleRefresh()
  }

  handleInputChange(event) {
    // Handle changes to select, checkbox, or other inputs
    const target = event.target
    const tagName = target.tagName.toLowerCase()

    // Only trigger for certain input types that indicate content changes
    if (tagName === "select" ||
        (tagName === "input" && ["checkbox", "radio"].includes(target.type))) {
      // These are likely styling or setting changes - refresh after a delay
      this.scheduleRefresh()
    }
  }
}
